import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { FileUploadZone } from '@/components/import/FileUploadZone';
import { ImportPreviewDialog } from '@/components/import/ImportPreviewDialog';
import { ValidationErrorList, ValidationError } from '@/components/import/ValidationErrorList';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { exportFullZip } from '@/lib/api/export';
import { validateZipImport, processZipImport, ValidationResult } from '@/lib/api/import';
import { invalidateAllCaches } from '@/api/cacheInvalidation';
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';

type ImportState =
    | { status: 'idle' }
    | { status: 'validating' }
    | { status: 'preview'; result: ValidationResult; file: File }
    | { status: 'processing'; result: ValidationResult; file: File }
    | { status: 'success'; entitiesCreated: { ledgers: number; values: number; returns: number }; creditsDeducted: number }
    | { status: 'error'; errors: ValidationError[] };

export function DataPage() {
    usePageTitle('Data');
    const queryClient = useQueryClient();
    const { refreshProfile } = useAuth();

    const [isExporting, setIsExporting] = useState(false);
    const [importState, setImportState] = useState<ImportState>({ status: 'idle' });

    // Handle export download
    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            await exportFullZip();
            toast.success('Export started! Your download will begin shortly.');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Failed to export data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    }, []);

    // Handle file selection for import
    const handleFileSelected = useCallback(async (file: File) => {
        setImportState({ status: 'validating' });

        try {
            const result = await validateZipImport(file);

            if (!result.isValid || result.errors.length > 0) {
                // Parse errors into ValidationError format
                const errors: ValidationError[] = result.errors.map((error, index) => {
                    // Try to extract row number from error message
                    const rowMatch = error.match(/Row (\d+)/);
                    const rowStr = rowMatch?.[1];
                    const row = rowStr ? parseInt(rowStr, 10) : index + 1;

                    // Try to extract field from error message
                    const fieldMatch = error.match(/^([A-Za-z]+):/);
                    const field = fieldMatch?.[1] ?? 'general';

                    return { row, field, message: error };
                });

                setImportState({ status: 'error', errors });
                return;
            }

            setImportState({ status: 'preview', result, file });
        } catch (error) {
            console.error('Validation failed:', error);
            toast.error('Failed to validate file. Please try again.');
            setImportState({ status: 'idle' });
        }
    }, []);

    // Handle import confirmation
    const handleConfirmImport = useCallback(async () => {
        if (importState.status !== 'preview') return;

        const { file, result } = importState;
        setImportState({ status: 'processing', result, file });

        try {
            const importResult = await processZipImport(file);

            if (importResult.success) {
                // Clear all caches after import - new data has been added
                invalidateAllCaches(queryClient);
                // Refresh auth profile to update hasAssetCollections/hasDebtCollections flags
                await refreshProfile();
                setImportState({
                    status: 'success',
                    entitiesCreated: importResult.entitiesCreated,
                    creditsDeducted: importResult.creditsDeducted,
                });
            } else {
                toast.error('Import failed. Please try again.');
                setImportState({ status: 'idle' });
            }
        } catch (error) {
            console.error('Import failed:', error);
            toast.error('Import failed. Please try again.');
            setImportState({ status: 'idle' });
        }
    }, [importState, queryClient, refreshProfile]);

    // Handle cancel/close preview dialog
    const handleCancelImport = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Handle clear file after error
    const handleClearError = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Handle starting a new import after success
    const handleNewImport = useCallback(() => {
        setImportState({ status: 'idle' });
    }, []);

    // Check if dialog should be open
    const isDialogOpen = importState.status === 'preview' || importState.status === 'processing';
    const isProcessing = importState.status === 'processing';

    return (
        <Layout>
            <div className="space-y-6">
                <PageHeader title="Data" />

                {/* Export and Import side-by-side on desktop */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Export Section */}
                    <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary text-gray-600 dark:text-gray-300">
                            <ArrowDownTrayIcon className="h-5 w-5" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Export
                        </h2>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Download a complete backup of your account data. The ZIP file includes
                        your assets, debts, values, returns, collections, and tags.
                    </p>

                    <Button
                        variant="secondary"
                        onClick={handleExport}
                        isLoading={isExporting}
                        disabled={isExporting}
                    >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download Full Backup (ZIP)
                    </Button>
                </section>

                {/* Import Section */}
                <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-secondary">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary text-gray-600 dark:text-gray-300">
                            <ArrowUpTrayIcon className="h-5 w-5" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Import
                        </h2>
                    </div>

                    {importState.status === 'success' ? (
                        /* Success State */
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-green-800 dark:text-green-300">
                                        Import Successful!
                                    </p>
                                    <div className="mt-2 text-sm text-green-700 dark:text-green-400 space-y-1">
                                        {importState.entitiesCreated.ledgers > 0 && (
                                            <p>{importState.entitiesCreated.ledgers} assets/debts created</p>
                                        )}
                                        {importState.entitiesCreated.values > 0 && (
                                            <p>{importState.entitiesCreated.values} values created</p>
                                        )}
                                        {importState.entitiesCreated.returns > 0 && (
                                            <p>{importState.entitiesCreated.returns} returns created</p>
                                        )}
                                        <p className="pt-1 border-t border-green-200 dark:border-green-800 mt-1">
                                            {importState.creditsDeducted} credits deducted
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <Button variant="secondary" onClick={handleNewImport}>
                                Import Another File
                            </Button>
                        </div>
                    ) : importState.status === 'error' ? (
                        /* Error State */
                        <div className="space-y-4">
                            <ValidationErrorList errors={importState.errors} />
                            <Button variant="secondary" onClick={handleClearError}>
                                Try Again
                            </Button>
                        </div>
                    ) : (
                        /* Normal/Validating State */
                        <>
                            <p className="text-gray-600 dark:text-gray-400 mb-2">
                                Import data from a previously exported ZIP file. Collections and tags
                                will be matched by name and reused if they already exist.
                            </p>
                            <p className="mb-4">
                                <KnowledgeLink page="import-export">
                                    Learn about import & export
                                </KnowledgeLink>
                            </p>

                            <FileUploadZone
                                acceptedTypes={['.zip']}
                                maxSizeMB={5}
                                onFileSelected={handleFileSelected}
                                disabled={importState.status === 'validating'}
                            />

                            {importState.status === 'validating' && (
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                                    Validating file...
                                </p>
                            )}
                        </>
                    )}
                </section>
                </div>

                {/* Link to Purge page */}
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                    Need to start fresh?{' '}
                    <Link
                        to="/data/purge"
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:underline"
                    >
                        Purge all data
                    </Link>
                </div>
            </div>

            {/* Import Preview Dialog */}
            {isDialogOpen && (importState.status === 'preview' || importState.status === 'processing') && (
                <ImportPreviewDialog
                    open={isDialogOpen}
                    validationResult={importState.result}
                    onConfirm={handleConfirmImport}
                    onCancel={handleCancelImport}
                    isProcessing={isProcessing}
                />
            )}
        </Layout>
    );
}
